import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { EntityHeaderPattern } from "../../patterns/EntityHeaderPattern";
import { OperationBoardPattern } from "../../patterns/OperationBoardPattern";
import { EntityWorkspace } from "../../layouts/EntityWorkspace";
import { AppShell } from "../../layouts/AppShell";
import { EntityListSidebar } from "../../layouts/EntityListSidebar";
import { Tabs } from "../../primitives/Tabs";
import { NotesList } from "../../components/NotesList";
import { ClientOverviewSection } from "../../components/client/ClientOverviewSection";
import { ClientNotesList } from "../../components/client/ClientNotesList";
import { ClientContactsSection } from "../../components/client/ClientContactsSection";
import { Button } from "../../primitives/Button";
import type { EntityWorkspaceBoardConfig } from "./entityWorkspaceBoardConfigs";

type GenericEntityWorkspacePageProps = {
  config: EntityWorkspaceBoardConfig;
  userName?: string;
  addActionLabel?: string;
  onAddAction?: () => void;
  items?: EntityWorkspaceBoardConfig["entityListItems"];
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  customContent?: ReactNode;
  onEntityUpdate?: () => void;
  onNewSubmission?: () => void;
  /** Use full-width container for workspace (catalog, inventory, orders, ops) */
  containerFluid?: boolean;
};

export function GenericEntityWorkspacePage({
  config,
  userName = "Utilisateur",
  addActionLabel,
  onAddAction,
  items,
  selectedId: controlledSelectedId,
  onSelect,
  customContent,
  onEntityUpdate,
  onNewSubmission,
  containerFluid = false,
}: GenericEntityWorkspacePageProps) {
  const [internalSelectedId, setInternalSelectedId] = useState<string | null>(null);
  const [listItems, setListItems] = useState(items ?? config.entityListItems);
  
  useEffect(() => {
    if (items) {
      setListItems(items);
    }
  }, [items]);
  
  useEffect(() => {
    // Update internal selectedId when entities change and no controlled selectedId
    if (!controlledSelectedId && config.entities.length > 0 && !internalSelectedId) {
      setInternalSelectedId(config.entities[0].id);
    } else if (!controlledSelectedId && config.entities.length === 0) {
      // Clear selection if no entities
      setInternalSelectedId(null);
    }
  }, [config.entities, controlledSelectedId, internalSelectedId]);
  
  const resolvedSelectedId = controlledSelectedId ?? internalSelectedId;
  const selectedEntity = useMemo(
    () => config.entities.find((entity) => entity.id === resolvedSelectedId) ?? null,
    [config.entities, resolvedSelectedId]
  );
  const [activeSectionId, setActiveSectionId] = useState<string>(
    config.sections.find((section) => section.active)?.id ?? config.boardSectionId
  );

  const sidebarItems = config.sections.map((section) => ({
    id: section.id,
    label: section.label,
    icon: section.icon,
    active: section.id === activeSectionId,
    onClick: () => setActiveSectionId(section.id),
  }));

  const activeSection = config.sections.find((section) => section.id === activeSectionId);

  return (
    <AppShell
      topbar={selectedEntity ? selectedEntity.header.title : config.topbarTitle}
      userName={userName}
      containerFluid={containerFluid}
      modules={[
        { label: "Commercial", sectionHeader: true },
        { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
        { label: "Clients", onClick: () => (window.location.hash = "#/clients") },
        { label: "Ambassadeurs", onClick: () => (window.location.hash = "#/ambassadors") },
        { label: "Pilotes", onClick: () => (window.location.hash = "#/pilots") },
        { label: "Soumissions" },
        { label: "KPIs" },
        { label: "Paramètres" },
        { label: "Opération", sectionHeader: true, separator: true },
        { label: "Opérations", onClick: () => (window.location.hash = "#/operations") },
        { label: "Tâches", onClick: () => (window.location.hash = "#/tasks") },
        { label: "Inventaires", onClick: () => (window.location.hash = "#/inventories") },
        { label: "Administration", sectionHeader: true, separator: true },
        { label: "Utilisateurs", onClick: () => (window.location.hash = "#/admin") },
        { label: "Services et Produits", onClick: () => (window.location.hash = "#/admin-products") },
        { label: "Promotions", onClick: () => (window.location.hash = "#/admin-promotions") },
        { label: "Processus", onClick: () => (window.location.hash = "#/admin-processes") },
        { label: "Templates de courriel", onClick: () => (window.location.hash = "#/admin-email-templates") },
        { label: "Facturation", onClick: () => (window.location.hash = "#/admin-billing") },
      ]}
    >
      <EntityWorkspace
        header={
          config.headerSlot ?? 
          (selectedEntity ? (
            <EntityHeaderPattern
              title={selectedEntity.header.title}
              metaItems={selectedEntity.header.metaItems}
              status={selectedEntity.header.status}
              primaryAction={selectedEntity.header.primaryAction}
              secondaryActions={selectedEntity.header.secondaryActions}
            />
          ) : null)
        }
        sidebarTitle={config.listTitle}
        sidebarIcon={config.listIcon}
        sidebarMeta={listItems.length ? `${listItems.length}` : undefined}
        sidebarItems={[]}
        storageKey={config.storageKey}
        defaultCollapsed={config.defaultCollapsed}
        defaultPinned={config.defaultPinned}
        allowHoverOpen={config.allowHoverOpen}
        renderSidebar={(isExpanded) => (
          <EntityListSidebar
            sections={[]}
            items={listItems}
            selectedId={resolvedSelectedId}
            onSelect={(id) => {
              if (onSelect) {
                onSelect(id);
              } else {
                setInternalSelectedId(id);
              }
            }}
            isExpanded={isExpanded}
            searchPlaceholder={config.searchPlaceholder ?? `Rechercher ${config.entityType}`}
            filterOptions={["Tous", "Actif", "Inactif"]}
            emptyMessage="Aucun client disponible."
            addActionLabel={addActionLabel}
            onAddAction={onAddAction}
            defaultCollapsedGroups={config.defaultCollapsedGroups}
          />
        )}
        content={
          customContent ? (
            customContent
          ) : (
            <div className="space-y-4">
              <Tabs
                tabs={sidebarItems.map((item) => ({ id: item.id, label: item.label }))}
                activeId={activeSectionId}
                onChange={(id) => setActiveSectionId(id)}
                variant="underline"
              />
              {selectedEntity && activeSectionId === config.boardSectionId ? (
                <div className="space-y-4">
                  {config.entityType === "client" && activeSectionId === "submissions" ? (
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-text">Soumissions</h3>
                      <Button
                        label="+ Nouvelle soumission"
                        intent="primary"
                        size="sm"
                        onClick={() => {
                          (window as any).__openSubmissionModal?.();
                        }}
                      />
                    </div>
                  ) : null}
                  <OperationBoardPattern {...selectedEntity.board} />
                </div>
              ) : selectedEntity && activeSectionId === "notes" && config.entityType === "client" ? (
                <ClientNotesList farmId={selectedEntity.id} />
              ) : selectedEntity && activeSectionId === "contacts" && config.entityType === "client" ? (
                <ClientContactsSection farmId={selectedEntity.id} />
              ) : selectedEntity && activeSectionId === "notes" ? (
                <NotesList
                  entityType={
                    config.entityType === "ambassador"
                      ? "ambassador_org"
                      : config.entityType === "pilot"
                        ? "drone_pilot_org"
                        : config.entityType
                  }
                  entityId={selectedEntity.id}
                />
              ) : selectedEntity && activeSectionId === "overview" && config.entityType === "client" ? (
                <ClientOverviewSection
                  farmId={selectedEntity.id}
                  onUpdate={onEntityUpdate}
                />
              ) : selectedEntity ? (
                <div className="rounded-md border border-border bg-surface p-6 shadow-sm text-sm text-muted">
                  {activeSection ? `Section: ${activeSection.label}` : "Section sélectionnée."}
                </div>
              ) : (
                <div className="rounded-md border border-border bg-surface p-6 shadow-sm text-sm text-muted">
                  Aucune entité sélectionnée.
                </div>
              )}
            </div>
          )
        }
      />
    </AppShell>
  );
}
